import { __awaiter } from "tslib";
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ReducerType } from '../types';
import { MISSING_CUSTOMER } from '@open-tender/types';
import { checkAuth, selectToken } from './account';
import { showNotification } from '../notifications';
import { makeFavoritesLookup } from '@open-tender/utils';
const initialState = {
    entities: [],
    error: null,
    loading: 'idle',
    lookup: {}
};
export var CustomerFavoritesActionType;
(function (CustomerFavoritesActionType) {
    CustomerFavoritesActionType["FetchCustomerFavorites"] = "customer/fetchCustomerFavorites";
    CustomerFavoritesActionType["UpdateCustomerFavorite"] = "customer/updateCustomerFavorite";
    CustomerFavoritesActionType["RemoveCustomerFavorite"] = "customer/removeCustomerFavorite";
    CustomerFavoritesActionType["AddCustomerFavorite"] = "customer/addCustomerFavorite";
})(CustomerFavoritesActionType || (CustomerFavoritesActionType = {}));
export const fetchCustomerFavorites = createAsyncThunk(CustomerFavoritesActionType.FetchCustomerFavorites, (_, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const { data: favorites } = yield api.getCustomerFavorites(token);
        const lookup = makeFavoritesLookup(favorites);
        dispatch(setCustomerFavoritesLookup(lookup));
        return favorites;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const updateCustomerFavorite = createAsyncThunk(CustomerFavoritesActionType.UpdateCustomerFavorite, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.putCustomerFavorite(token, requestData.favoriteId, requestData.data);
        const { data: favorites } = yield api.getCustomerFavorites(token);
        const lookup = makeFavoritesLookup(favorites);
        dispatch(setCustomerFavoritesLookup(lookup));
        dispatch(showNotification('Favorite updated!'));
        if (requestData.callback)
            requestData.callback();
        return favorites;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const removeCustomerFavorite = createAsyncThunk(CustomerFavoritesActionType.RemoveCustomerFavorite, (requestData, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        yield api.deleteCustomerFavorite(token, requestData.favoriteId);
        const { data: favorites } = yield api.getCustomerFavorites(token);
        const lookup = makeFavoritesLookup(favorites);
        dispatch(setCustomerFavoritesLookup(lookup));
        if (requestData.callback)
            requestData.callback();
        return favorites;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
export const addCustomerFavorite = createAsyncThunk(CustomerFavoritesActionType.AddCustomerFavorite, ({ item, name, callback }, { dispatch, getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const api = getState().config.api;
        const token = selectToken(getState());
        if (!token)
            throw new Error(MISSING_CUSTOMER);
        const data = { cart: item, name: name || '' };
        yield api.postCustomerFavorite(token, data);
        const { data: favorites } = yield api.getCustomerFavorites(token);
        const lookup = makeFavoritesLookup(favorites);
        dispatch(setCustomerFavoritesLookup(lookup));
        if (callback)
            callback();
        return favorites;
    }
    catch (err) {
        const error = err;
        return checkAuth(error, dispatch, () => rejectWithValue(error));
    }
}));
const customerFavoritesSlice = createSlice({
    name: ReducerType.Favorites,
    initialState,
    reducers: {
        resetCustomerFavorites: () => initialState,
        resetCustomerFavoritesError: state => {
            state.error = null;
            state.loading = 'idle';
        },
        setCustomerFavorites: (state, action) => {
            state.entities = action.payload;
            state.error = null;
        },
        setCustomerFavoritesLookup: (state, action) => {
            state.lookup = action.payload;
            state.error = null;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchCustomerFavorites.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(fetchCustomerFavorites.pending, state => {
            state.loading = 'pending';
        })
            .addCase(fetchCustomerFavorites.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(updateCustomerFavorite.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(updateCustomerFavorite.pending, state => {
            state.loading = 'pending';
        })
            .addCase(updateCustomerFavorite.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(removeCustomerFavorite.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(removeCustomerFavorite.pending, state => {
            state.loading = 'pending';
        })
            .addCase(removeCustomerFavorite.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        })
            .addCase(addCustomerFavorite.fulfilled, (state, action) => {
            state.entities = action.payload;
            state.loading = 'idle';
            state.error = null;
        })
            .addCase(addCustomerFavorite.pending, state => {
            state.loading = 'pending';
        })
            .addCase(addCustomerFavorite.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = 'idle';
        });
    }
});
export const { resetCustomerFavorites, resetCustomerFavoritesError, setCustomerFavorites, setCustomerFavoritesLookup } = customerFavoritesSlice.actions;
export const selectCustomerFavorites = (state) => state.customer.favorites;
export const customerFavoritesReducer = customerFavoritesSlice.reducer;
